// @ts-nocheck
// src/components/gamification/GamifiedProfileCard.tsx - Beautiful ReactBits-style profile card
import { motion } from 'framer-motion';
import { Eye, Heart, TrendingUp, Users, BarChart3, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/utils/formatNumber';
import { GamificationBadge } from './GamificationBadge';

interface SocialMetrics {
	followers: number;
	following: number;
	posts: number;
	engagement: number;
	views: number;
	engagementRate: number;
	growthRate: number;
}

interface GamifiedProfileCardProps {
	profile: {
		_id: string;
		profileTitle: string;
		profilePicture?: string;
		username?: string;
		isGameEnabled?: boolean;
		currentTier?: string;
		totalScore?: number;
	};
	metrics?: SocialMetrics;
	platform?: string;
	isLoading?: boolean;
	onRefresh?: () => void;
	onViewDetails?: () => void;
	canEdit?: boolean;
}

const platformColors: Record<string, string> = {
	x: 'from-blue-400 to-blue-600',
	twitter: 'from-blue-400 to-blue-600',
	twitterx: 'from-blue-400 to-blue-600',
	'twitter x': 'from-blue-400 to-blue-600',
	facebook: 'from-blue-600 to-blue-800',
	instagram: 'from-pink-500 to-purple-600',
	linkedin: 'from-blue-700 to-blue-900',
	youtube: 'from-red-500 to-red-700',
	tiktok: 'from-black to-gray-800',
};

const platformIcons: Record<string, string> = {
	x: '𝕏',
	twitter: '𝕏',
	twitterx: '𝕏',
	'twitter x': '𝕏',
	facebook: 'f',
	instagram: '📷',
	linkedin: 'in',
	youtube: '▶️',
	tiktok: '♪',
};

const toMetricNumber = (value: unknown): number => {
	const numeric = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(numeric) ? numeric : 0;
};

export function GamifiedProfileCard({
	profile,
	metrics,
	platform = 'twitter',
	isLoading = false,
	onRefresh,
	onViewDetails,
	canEdit = false,
}: GamifiedProfileCardProps) {
	if (isLoading) {
		return (
			<Card className="relative overflow-hidden p-6 bg-gradient-to-br from-gray-900 to-gray-800">
				<div className="space-y-4">
					<div className="flex items-start justify-between">
						<div className="flex items-center gap-4">
							<Skeleton className="h-20 w-20 rounded-full" />
							<div className="space-y-2">
								<Skeleton className="h-6 w-32" />
								<Skeleton className="h-4 w-24" />
							</div>
						</div>
						<Skeleton className="h-8 w-8 rounded-full" />
					</div>
					<div className="grid grid-cols-3 gap-4">
						<Skeleton className="h-16" />
						<Skeleton className="h-16" />
						<Skeleton className="h-16" />
					</div>
				</div>
			</Card>
		);
	}

	// Normalize platform name to lowercase for consistent matching
	const normalizedPlatform = platform?.toLowerCase() || 'twitter';
	const gradientClass = platformColors[normalizedPlatform] || platformColors.twitter;
	const platformIcon = platformIcons[normalizedPlatform] || '🌐';
	const safeMetrics = metrics
		? {
				followers: toMetricNumber(metrics.followers),
				following: toMetricNumber(metrics.following),
				posts: toMetricNumber(metrics.posts),
				engagement: toMetricNumber(metrics.engagement),
				views: toMetricNumber(metrics.views),
				engagementRate: toMetricNumber(metrics.engagementRate),
				growthRate: toMetricNumber(metrics.growthRate),
			}
		: null;

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.3 }}
		>
			<Card className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-gray-700 hover:border-gray-600 transition-all duration-300">
				{/* Background gradient effect */}
				<div className={cn(
					"absolute inset-0 opacity-10 bg-gradient-to-br",
					gradientClass
				)} />
				
				{/* Main content */}
				<div className="relative p-6 space-y-6">
					{/* Header with profile info */}
					<div className="flex items-start justify-between">
						<div className="flex items-center gap-4">
							{/* Profile picture */}
							<div className="relative">
								<div className={cn(
									"absolute inset-0 rounded-full bg-gradient-to-br blur-xl opacity-50",
									gradientClass
								)} />
								<img
									src={profile.profilePicture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.profileTitle}`}
									alt={profile.profileTitle}
									className="relative h-20 w-20 rounded-full border-2 border-gray-700 object-cover"
								/>
								{/* Platform icon */}
								<div className={cn(
									"absolute -bottom-1 -right-1 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold text-white bg-gradient-to-br",
									gradientClass
								)}>
									{platformIcon}
								</div>
							</div>
							
							{/* Profile name and username */}
							<div>
								<h3 className="text-xl font-bold text-white">
									{profile.profileTitle}
								</h3>
								{profile.username && (
									<p className="text-sm text-gray-400">
										@{profile.username}
									</p>
								)}
								{profile.isGameEnabled && (
									<div className="mt-2">
										<GamificationBadge
											score={profile.totalScore || 0}
											tier={profile.currentTier || 'bronze'}
											size="sm"
											showScore={false}
										/>
									</div>
								)}
							</div>
						</div>
						
						{/* Action buttons */}
						<div className="flex gap-2">
							{onRefresh && (
								<Button
									size="icon"
									variant="ghost"
									onClick={onRefresh}
									className="text-gray-400 hover:text-white"
								>
									<BarChart3 className="h-4 w-4" />
								</Button>
							)}
						</div>
					</div>

					{/* Metrics grid */}
						{safeMetrics && (
							<div className="grid grid-cols-3 gap-4">
							{/* Followers */}
							<motion.div
								whileHover={{ scale: 1.05 }}
								className="bg-gray-800/50 rounded-lg p-4 border border-gray-700"
							>
								<div className="flex items-center justify-between mb-2">
									<Users className="h-4 w-4 text-gray-400" />
										{safeMetrics.growthRate > 0 && (
											<Badge variant="secondary" className="text-xs bg-green-900/20 text-green-400">
												+{safeMetrics.growthRate.toFixed(1)}%
											</Badge>
										)}
									</div>
									<p className="text-2xl font-bold text-white">
										{formatNumber(safeMetrics.followers)}
									</p>
								<p className="text-xs text-gray-400">Followers</p>
							</motion.div>

							{/* Engagement */}
							<motion.div
								whileHover={{ scale: 1.05 }}
								className="bg-gray-800/50 rounded-lg p-4 border border-gray-700"
							>
								<div className="flex items-center justify-between mb-2">
										<Heart className="h-4 w-4 text-gray-400" />
										<Badge variant="secondary" className="text-xs bg-purple-900/20 text-purple-400">
											{safeMetrics.engagementRate.toFixed(1)}%
										</Badge>
									</div>
									<p className="text-2xl font-bold text-white">
										{formatNumber(safeMetrics.engagement)}
									</p>
								<p className="text-xs text-gray-400">Engagement</p>
							</motion.div>

							{/* Views */}
							<motion.div
								whileHover={{ scale: 1.05 }}
								className="bg-gray-800/50 rounded-lg p-4 border border-gray-700"
							>
								<div className="flex items-center justify-between mb-2">
									<Eye className="h-4 w-4 text-gray-400" />
									<TrendingUp className="h-4 w-4 text-blue-400" />
									</div>
									<p className="text-2xl font-bold text-white">
										{formatNumber(safeMetrics.views)}
									</p>
								<p className="text-xs text-gray-400">Views</p>
							</motion.div>
						</div>
					)}

					{/* Engagement breakdown */}
						{safeMetrics && (
							<div className="space-y-3">
								<div className="flex items-center justify-between text-sm">
									<span className="text-gray-400">Posts</span>
									<span className="text-white font-medium">{formatNumber(safeMetrics.posts)}</span>
								</div>
							
							{/* Engagement rate progress bar */}
							<div className="space-y-2">
									<div className="flex items-center justify-between text-sm">
										<span className="text-gray-400">Engagement Rate</span>
										<span className="text-white font-medium">{safeMetrics.engagementRate.toFixed(2)}%</span>
									</div>
									<Progress
										value={Math.min(safeMetrics.engagementRate * 10, 100)}
										className="h-2 bg-gray-700"
									/>
							</div>
						</div>
					)}

					{/* Action buttons */}
					<div className="flex gap-2 pt-2">
						{onViewDetails && (
							<Button
								onClick={onViewDetails}
								className={cn(
									"flex-1 bg-gradient-to-r text-white border-0",
									gradientClass
								)}
							>
								<Sparkles className="h-4 w-4 mr-2" />
								View Details
							</Button>
						)}
						{canEdit && (
							<Button
								variant="outline"
								className="flex-1 border-gray-700 hover:bg-gray-800"
							>
								Manage
							</Button>
						)}
					</div>
				</div>

				{/* Decorative elements */}
				<div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-3xl" />
				<div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-full blur-3xl" />
			</Card>
		</motion.div>
	);
}
